import {
  defineComponent, ref, watch, computed, Transition, Teleport, PropType, cloneVNode,
} from 'vue'
import { Text } from '../Text'
import { Icon } from '../Icon'
import { Space } from '../Space'
import { ClientOnly } from '../ClientOnly'
import { Close } from '../../icons'
import { DrawerPlacement, DrawerSize } from '../../enums'

export default defineComponent({
  inheritAttrs: false,
  props: {
    title: {
      type: String,
      default: undefined,
    },
    placement: {
      type: String as PropType<DrawerPlacement>,
      default: 'right',
    },
    size: {
      type: [String, Number] as PropType<DrawerSize | string | number>,
      default: 'default',
    },
    closeable: {
      type: Boolean,
      default: true,
    },
    visible: {
      type: Boolean,
      default: false,
    },
    outsideCloseable: {
      type: Boolean,
      default: false,
    },
    mask: {
      type: Boolean,
      default: false,
    },
    maskCloseable: {
      type: Boolean,
      default: false,
    },
  },
  emits: {
    'update:visible'(visible: boolean) {
      return visible !== undefined
    },
  },
  setup(props, { slots, attrs, emit }) {
    const visible = ref(props.visible)

    watch(
      () => props.visible,
      v => {
        visible.value = v
      },
    )

    watch(
      visible,
      v => emit('update:visible', v),
    )

    const isStandardSize = computed(
      () => typeof props.size === 'string' && ['default', 'large'].includes(props.size),
    )

    const isVertical = computed(
      () => ['top', 'bottom'].includes(props.placement),
    )

    const drawer = ref<HTMLElement>()

    function drawerClose(event: MouseEvent) {
      if (props.mask ? props.maskCloseable : props.outsideCloseable) {
        if (!event.composedPath().includes(drawer.value)) {
          visible.value = false
        }
      }
    }

    const withHeader = !!(slots.title || props.title || props.closeable)

    return () => {
      const content = cloneVNode(
        <div
          ref={drawer}
          class={
            [
              'd-drawer',
              `d-drawer-${props.placement}`,
              isStandardSize.value && `d-drawer-${props.size}`,
              !withHeader && 'd-drawer-no-header',
              attrs.class,
            ]
          }
          style={
            [
              !isStandardSize.value
                ? {
                  [isVertical.value ? 'height' : 'width']: typeof props.size === 'number'
                    ? `${props.size}px`
                    : props.size,
                }
                : undefined,
              attrs.style,
            ]
          }
        >
          {
            withHeader && <Space class={'d-drawer-header'} size={'small'}>
              {
                slots.title?.() || <Text
                  type={'h6'}
                  bold
                >
                  {props.title}
                </Text>
              }
              {
                props.closeable && <Icon
                  class={
                    [
                      'd-drawer-close',
                      'd-clickable',
                    ]
                  }
                  color={'current'}
                  icon={Close}
                  {
                    ...{
                      onClick() {
                        visible.value = false
                      },
                    }
                  }
                />
              }
            </Space>
          }
          <div class={'d-drawer-content'}>
            {slots.default?.()}
          </div>
        </div>,
        {
          onVnodeMounted() {
            setTimeout(
              () => document.addEventListener('click', drawerClose),
              0,
            )
          },
          onVnodeBeforeUnmount() {
            document.removeEventListener('click', drawerClose)
          },
        },
      )

      return <ClientOnly
        v-slots={{
          default: () => <Teleport to={'body'}>
            <>
              {
                props.mask && <Transition name={'portal-fade'}>
                  {visible.value && <div class={'d-drawer-mask'}/>}
                </Transition>
              }
              <Transition name={`drawer-slide-${props.placement}`}>
                {visible.value && content}
              </Transition>
            </>
          </Teleport>,
          alter: () => <Teleport to={'body'}>
            {
              visible.value && (
                props.mask
                  ? <div class={'d-drawer-mask'}>{content}</div>
                  : content
              )
            }
          </Teleport>,
        }}
      />
    }
  },
})
